import FixedFooter from 'components/custom/onboarding/fixed-footer';
import React from 'react';
import { useTranslation } from 'react-i18next';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Collapse, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { JobTypeEnum } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { RouterButton } from '@libs/ui/components/button';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { formatRate } from '@libs/ui/components/job/utils';

import { RATES } from '../consts';
import { useDefaultOnSubmitHandler } from '../form-context/hooks';
import { BuilderFormState } from '../form-context/types';
import NextButtonWithValidation from '../shared/NextButtonWithValidation';
import StepTemplate from '../shared/StepTemplate';
import { useJobTypeState } from '../shared/hooks';
import { DefaultStepProps } from '../shared/types';

interface JobRateProps extends DefaultStepProps {}

const useStyles = makeStyles((theme) => ({
  form: {
    maxWidth: 430,
    margin: '0 auto',

    '& h6': {
      textAlign: 'right',
    },

    '& .MuiFormHelperText-root': {
      color: 'white',
      textAlign: 'right',
      marginRight: 0,

      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },
  },
  fields: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}));

const JobRate = ({ index, nextStepRoute, prevStepRoute }: JobRateProps) => {
  const classes = useStyles();
  const { t } = useTranslation('companyOnboarding');
  const TEXTS = {
    [JobTypeEnum.Freelance]: {
      title: t('new.jobRate.selectHourlyRate'),
      subtitle: t('new.jobRate.setMinMax'),
      currentStepLabel: t('new.jobRate.rate'),
    },
    [JobTypeEnum.Permanent]: {
      title: t('new.jobRate.selectMonthlySalary'),
      subtitle: t('new.jobRate.setMinMax'),
      currentStepLabel: t('new.jobRate.salary'),
    },
    [JobTypeEnum.Project]: {
      title: t('new.jobRate.selectMonthlySalary'),
      subtitle: t('new.jobRate.setMinMax'),
      currentStepLabel: t('new.jobRate.salary'),
    },
  };
  const jobType = useJobTypeState();
  const currentRates = RATES[jobType];
  const { onSubmit, loading } = useDefaultOnSubmitHandler({
    redirectRoute: nextStepRoute,
  });
  const currentTexts = TEXTS[jobType];

  return (
    <StepTemplate {...currentTexts} currentStep={index}>
      <Box className={classes.form}>
        <Collapse in={jobType === JobTypeEnum.Freelance}>
          <Grid className={classes.fields} spacing={4} container>
            <Grid xs={6} item>
              <ConnectedTextField
                name={modelPath<BuilderFormState>((m) => m.rate.minRate)}
                data-test-id="firstName"
                fullWidth
                variant="filled"
                label={t('new.jobRate.minRate')}
              />
            </Grid>
            <Grid xs={6} item>
              <ConnectedTextField
                name={modelPath<BuilderFormState>((m) => m.rate.maxRate)}
                data-test-id="firstName"
                fullWidth
                variant="filled"
                label={t('new.jobRate.maxRate')}
                helperText={`Max. ${formatRate({
                  min: currentRates.max,
                  period: 'hour',
                })}`}
              />
            </Grid>
          </Grid>
        </Collapse>

        <Collapse in={jobType === JobTypeEnum.Permanent}>
          <Grid className={classes.fields} spacing={4} container>
            <Grid xs={12} sm={6} item>
              <ConnectedTextField
                name={modelPath<BuilderFormState>((m) => m.rate.minSalary)}
                data-test-id="firstName"
                fullWidth
                variant="filled"
                label={t('new.jobRate.minSalary')}
              />
            </Grid>
            <Grid xs={12} sm={6} item>
              <ConnectedTextField
                name={modelPath<BuilderFormState>((m) => m.rate.maxSalary)}
                data-test-id="firstName"
                fullWidth
                variant="filled"
                label={t('new.jobRate.maxSalary')}
                helperText={`Max. ${formatRate({
                  min: currentRates.max,
                  period: 'month',
                })}`}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Box>

      <FixedFooter>
        <Grid spacing={4} justifyContent="center" container>
          <Grid xs={6} item>
            <RouterButton
              startIcon={<ArrowBackIcon />}
              to={prevStepRoute}
              color="primary"
              variant="outlined"
              fullWidth
            >
              {t('common.back')}
            </RouterButton>
          </Grid>
          <Grid xs={6} item>
            <NextButtonWithValidation
              path={modelPath<BuilderFormState>((m) => m.workspace)}
              disabled={loading}
              onClick={onSubmit}
              endIcon={null}
            >
              {t('common.next')}
            </NextButtonWithValidation>
          </Grid>
        </Grid>
      </FixedFooter>
    </StepTemplate>
  );
};

export default JobRate;
